// Early native keyboard-geometry capture.
//
// The vortex-webview host emits the retained `vortex:keyboardgeometry` baseline
// as soon as a trusted document is ready — which can happen BEFORE any React
// effect listener is mounted. To remove that race, this module registers a single
// document-lifetime `window` listener as an intentional side effect at import
// time, and must be imported from `main.tsx` before React root creation.
//
// It validates every event through the same pure contract parser the provider
// uses, retains only the latest valid physical payload, and lets the provider
// (a) read the latest retained payload synchronously and (b) subscribe for future
// payloads — with no gap between the read and the subscription. It holds no
// business state, no storage, no timers, no polling, and no page-specific logic.
//
// @docs:
// - docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md

import {
  KEYBOARD_GEOMETRY_EVENT,
  parseNativePayload,
  type NativeKeyboardPayload,
} from './deriveViewport';

type PayloadListener = (payload: NativeKeyboardPayload) => void;

let latest: NativeKeyboardPayload | null = null;
const listeners = new Set<PayloadListener>();
let registered = false;

function handleEvent(event: Event): void {
  const detail = (event as CustomEvent).detail;
  const payload = parseNativePayload(detail);
  if (!payload) return; // malformed: never replace the last valid payload or notify
  latest = payload;
  for (const listener of listeners) listener(payload);
}

/** Register the single document-lifetime listener (idempotent, browser-only). */
function ensureRegistered(): void {
  if (registered || typeof window === 'undefined') return;
  registered = true;
  window.addEventListener(KEYBOARD_GEOMETRY_EVENT, handleEvent);
}

// Intentional side effect: begin capturing before React mounts.
ensureRegistered();

/** The latest valid native payload captured so far, or null if none yet. */
export function getLatestNativePayload(): NativeKeyboardPayload | null {
  return latest;
}

/**
 * Subscribe to future valid native payloads. Returns an unsubscribe function.
 * The provider should read {@link getLatestNativePayload} and subscribe in the
 * same synchronous step so no event is missed between the two.
 */
export function subscribeNativePayload(listener: PayloadListener): () => void {
  ensureRegistered();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
